import { c as _c } from "react/compiler-runtime";
/**
 * @fileoverview This file renders a section displaying checks (CI checks, code checks, etc.)
 * for a Pull Request, grouping them by required/optional and by status, allowing
 * the user to expand/collapse sections and see annotation details.
 *
 * The main entry point is the `CheckSection` component, which fetches data related to
 * check runs and merges them with the PR's required checks to display a structured
 * overview of all checks.
 */

import type { CheckRunData } from '@/data/check-runs/use-check-runs-by-commit';
import type { MergeabilityStatus } from '@/lib/hooks/mergeability-status/get-mergeability-status.server';
import type { PrV2CheckRunResponse } from '@/lib/pr-v2/types';
import { ChevronDown, ChevronRight, CircleDashed } from 'lucide-react';
import { useState } from 'react';
import type { MergeDropdownCheckConclusion, MergeDropdownCheckRun, MergeDropdownCheckStatus } from './types';
import { getCheckIcon } from '../../merge-dropdown-utils';
import { CheckRunRow } from './check-run-row';
import { MERGE_DROPDOWN_CHECK_CONCLUSION, MERGE_DROPDOWN_CHECK_STATUS } from './types';
import { groupChecksByRequirement, groupChecksByStatus, isCheckFailing, isCheckPassing } from './utils';
import { Spinner } from '@/components/ui/spinner';
import { usePrV2Checks } from '@/data/v2/pr/use-pr-v2-checks';
import { normalizePrV2CheckRunsForDisplay } from '@/lib/pr-v2/check-run-presentation';
import { usePrStore } from '@/lib/stores/pr-store';
function normalizeCheckStatus(value: unknown): MergeDropdownCheckStatus {
  return typeof value === 'string' && Object.values(MERGE_DROPDOWN_CHECK_STATUS).includes(value as MergeDropdownCheckStatus) ? value as MergeDropdownCheckStatus : MERGE_DROPDOWN_CHECK_STATUS.COMPLETED;
}
function normalizeCheckConclusion(value: unknown): MergeDropdownCheckConclusion | null {
  return typeof value === 'string' && Object.values(MERGE_DROPDOWN_CHECK_CONCLUSION).includes(value as MergeDropdownCheckConclusion) ? value as MergeDropdownCheckConclusion : null;
}
function normalizePrV2CheckRun(checkRun: PrV2CheckRunResponse): MergeDropdownCheckRun {
  return {
    id: checkRun.githubCheckRunId || checkRun.id,
    name: checkRun.name,
    status: normalizeCheckStatus(checkRun.status),
    conclusion: normalizeCheckConclusion(checkRun.conclusion),
    html_url: checkRun.detailsUrl,
    output: null,
    annotations: []
  };
}

/**
 * Renders the main check section for a Pull Request, including required/optional checks
 * grouped by status, plus a summary count. Also allows expanding/collapsing each group
 * and displays failing checks prominently if there are failures.
 *
 * @param {object} props
 * @param {MergeabilityStatus} props.mergeabilityStatus - An object describing the PR's mergeability checks status.
 * @returns {JSX.Element | null} A section displaying all checks, or `null` if no current check suite is found.
 */
type CheckSectionProps = {
  mergeabilityStatus?: MergeabilityStatus;
  headSha?: string | null;
};
export function CheckSection(t0) {
  const $ = _c(33);
  const {
    mergeabilityStatus,
    headSha: t1
  } = t0;
  const headSha = t1 === undefined ? null : t1;
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    owner,
    repo,
    number
  } = usePrStore();
  const t2 = Boolean(owner && repo && number);
  let t3;
  if ($[0] !== t2) {
    t3 = {
      enabled: t2
    };
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    data: prV2Checks,
    isLoading: prV2ChecksLoading,
    isError: prV2ChecksError
  } = usePrV2Checks(owner, repo, number, t3);
  const isLoading = prV2ChecksLoading;
  const isError = prV2ChecksError;
  if (isLoading) {
    let t4;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="flex items-center gap-2 text-sm text-muted-foreground py-2"><Spinner size="sm" className="text-muted-foreground" /><span>Loading checks...</span></div>;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    return t4;
  }
  if (isError) {
    let t4;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="text-sm text-red-500 py-2">Failed to load check status</div>;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    return t4;
  }
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[4] !== headSha || $[5] !== isExpanded || $[6] !== mergeabilityStatus?.checksStatus.requiredChecks || $[7] !== prV2Checks) {
    t8 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const allCheckRuns = normalizePrV2CheckRunsForDisplay({
        checks: prV2Checks ?? [],
        currentHeadSha: headSha
      }).map(normalizePrV2CheckRun);
      const requiredCheckNames = new Set(mergeabilityStatus?.checksStatus.requiredChecks ?? []);
      const existingCheckNames = new Set(allCheckRuns.map(_temp));
      const pendingRequiredChecks = [];
      requiredCheckNames.forEach(checkName => {
        if (!existingCheckNames.has(checkName)) {
          pendingRequiredChecks.push(checkName);
        }
      });
      if (!allCheckRuns.length && !pendingRequiredChecks.length) {
        t8 = null;
        break bb0;
      }
      const {
        required,
        optional
      } = groupChecksByRequirement(allCheckRuns, requiredCheckNames);
      const groupedChecks = {
        required: groupChecksByStatus(required),
        optional: groupChecksByStatus(optional)
      };
      const requiredChecks = allCheckRuns.filter(check_0 => requiredCheckNames.has(check_0.name));
      const optionalChecks = allCheckRuns.filter(check_1 => !requiredCheckNames.has(check_1.name));
      const requiredTotal = requiredCheckNames.size;
      const requiredPassed = requiredChecks.filter(isCheckPassing).length;
      const requiredFailed = requiredChecks.filter(isCheckFailing).length;
      const optionalTotal = optionalChecks.length;
      const optionalPassed = optionalChecks.filter(isCheckPassing).length;
      const optionalFailed = optionalChecks.filter(isCheckFailing).length;
      let checksSummary;
      if (requiredTotal > 0) {
        if (requiredFailed > 0) {
          checksSummary = `${requiredFailed}/${requiredTotal} required checks failed`;
        } else {
          checksSummary = `${requiredPassed}/${requiredTotal} required checks passed`;
        }
      } else {
        if (optionalFailed > 0) {
          checksSummary = `${optionalFailed}/${optionalTotal} optional checks failed`;
        } else {
          if (optionalTotal > 0) {
            checksSummary = `${optionalPassed}/${optionalTotal} optional checks passed`;
          } else {
            checksSummary = "No checks found";
          }
        }
      }
      const requiredGroups = groupedChecks.required;
      const optionalGroups = groupedChecks.optional;
      const iconColor = requiredFailed > 0 ? "text-red-500" : "text-muted-foreground";
      const overallStatus = pendingRequiredChecks.length > 0 && requiredFailed === 0 ? "in_progress" : "completed";
      const overallConclusion = requiredFailed > 0 ? "failure" : pendingRequiredChecks.length > 0 ? "neutral" : "success";
      t7 = "space-y-4 w-full";
      t4 = "space-y-3";
      let t9;
      if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = () => setIsExpanded(_temp2);
        $[13] = t9;
      } else {
        t9 = $[13];
      }
      let t10;
      if ($[14] !== iconColor || $[15] !== overallConclusion || $[16] !== overallStatus) {
        t10 = getCheckIcon({
          status: overallStatus,
          conclusion: overallConclusion
        }, iconColor);
        $[14] = iconColor;
        $[15] = overallConclusion;
        $[16] = overallStatus;
        $[17] = t10;
      } else {
        t10 = $[17];
      }
      let t11;
      if ($[18] !== checksSummary || $[19] !== t10) {
        t11 = <div className="flex items-center gap-2 text-sm">{t10}{checksSummary}</div>;
        $[18] = checksSummary;
        $[19] = t10;
        $[20] = t11;
      } else {
        t11 = $[20];
      }
      let t12;
      if ($[21] !== isExpanded) {
        t12 = isExpanded ? <ChevronDown className="size-4 text-muted-foreground" /> : <ChevronRight className="size-4 text-muted-foreground" />;
        $[21] = isExpanded;
        $[22] = t12;
      } else {
        t12 = $[22];
      }
      if ($[23] !== t11 || $[24] !== t12) {
        t5 = <div className="flex cursor-pointer select-none items-center justify-between py-2 hover:bg-zinc-800/50 rounded-sm px-1.5 -mx-1.5" onClick={t9}>{t11}{t12}</div>;
        $[23] = t11;
        $[24] = t12;
        $[25] = t5;
      } else {
        t5 = $[25];
      }
      t6 = isExpanded && <div className="ml-4 space-y-4 mt-2">{(Object.keys(requiredGroups).length > 0 || pendingRequiredChecks.length > 0) && <div className="ml-4 space-y-3"><div className="text-sm text-muted-foreground">Required</div>{Object.entries(requiredGroups).map(_temp4)}{pendingRequiredChecks.length > 0 && <div className="space-y-3">{pendingRequiredChecks.map(_temp5)}</div>}</div>}{Object.keys(optionalGroups).length > 0 && <div className="ml-4 space-y-3"><div className="text-sm text-muted-foreground">Optional</div>{Object.entries(optionalGroups).map(_temp7)}</div>}</div>;
    }
    $[4] = headSha;
    $[5] = isExpanded;
    $[6] = mergeabilityStatus?.checksStatus.requiredChecks;
    $[7] = prV2Checks;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
    t8 = $[12];
  }
  if (t8 !== Symbol.for("react.early_return_sentinel")) {
    return t8;
  }
  let t9;
  if ($[26] !== t4 || $[27] !== t5 || $[28] !== t6) {
    t9 = <div className={t4}>{t5}{t6}</div>;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== t7 || $[31] !== t9) {
    t10 = <div className={t7}>{t9}</div>;
    $[30] = t7;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  return t10;
}
function _temp7(t0) {
  const [status_0, runs_0] = t0;
  return <div key={`optional-${status_0}`} className="space-y-3">{runs_0.map(_temp6)}</div>;
}
function _temp6(checkRun_0) {
  return <CheckRunRow key={checkRun_0.id} checkRun={checkRun_0} />;
}
function _temp5(checkName_0) {
  return <div key={`pending-${checkName_0}`} className="flex items-center gap-2 text-sm text-zinc-200"><CircleDashed className="size-4 text-muted-foreground" /><span>{checkName_0} (Pending)</span></div>;
}
function _temp4(t0) {
  const [status, runs] = t0;
  return <div key={`required-${status}`} className="space-y-3">{runs.map(_temp3)}</div>;
}
function _temp3(checkRun) {
  return <CheckRunRow key={checkRun.id} checkRun={checkRun} />;
}
function _temp2(prev) {
  return !prev;
}
function _temp(check) {
  return check.name;
}